/**
 * The Documents tab for an organizational unit — teams and departments.
 *
 * There used to be two of these: `Teams/DocumentsTab.tsx` and
 * `Departments/DocumentsTab.tsx`, 391 lines each and identical apart from the
 * word "team"/"department" and the streaming `domain`. Both carried their own
 * `bg-gray-200 animate-fadePulse` skeleton bars, their own date formatting and
 * their own file-row layout.
 *
 * One component, `domain`-parameterized, drawing through the shared
 * attachment primitives. The endpoints stay with the pages that own them and
 * arrive as `onUpload` / `onDelete` / `onBulkDelete` / `onRefresh`.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiFileLight, PiTrashLight, PiFolderLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { useGlobalStreaming } from '../../../hooks/useGlobalStreaming';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { useOrgSurface } from './OrgSurfaceContext';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import {
  AttachmentDropzone,
  AttachmentList,
  toAttachments,
  filterAttachments,
  sortAttachments,
  Attachment,
} from '../../shared/attachments';
import Spinner from '../../shared/TailwindComponents/Spinner';
import { toErrorText } from '../../../utils/errorMessage';

export interface OrgUnitDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  uploadedBy: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  uploadedAt: string;
}

export interface DocumentsTabProps {
  /** Which streaming domain the files live under. */
  domain: 'teams' | 'departments';
  /** The team or department uuid. */
  resourceId: string;
  documents: OrgUnitDocument[];
  loading?: boolean;
  isUploading?: boolean;
  uploadingCount?: number;
  onUpload?: (files: File[]) => Promise<void>;
  onDelete?: (documentId: string) => Promise<void>;
  onBulkDelete?: (documentIds: string[]) => Promise<void>;
  onRefresh?: () => Promise<void>;
  'data-tour-id'?: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  domain,
  resourceId,
  documents = [],
  loading = false,
  isUploading = false,
  uploadingCount = 0,
  onUpload,
  onDelete,
  onBulkDelete,
  onRefresh,
  'data-tour-id': dataTourId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { viewFile, downloadFile } = useGlobalStreaming();
  /* Null outside a team/department surface — see `handleOpenDocument`. */
  const surface = useOrgSurface();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingTotal, setDeletingTotal] = useState(0);

  const normalized = useMemo(() => sortAttachments(toAttachments(documents)), [documents]);
  const visible = useMemo(() => filterAttachments(normalized, searchQuery), [normalized, searchQuery]);

  const rowOf = (attachment: Attachment): OrgUnitDocument =>
    (attachment.raw as OrgUnitDocument) ?? ({ ...attachment } as unknown as OrgUnitDocument);

  /**
   * Open a document in the READER PANE — or, outside a surface, in the
   * `fileReader` SIDE PANEL.
   *
   * This dispatched `showModal({ type: 'attachmentViewer' })`: a modal over
   * the whole page that covered the very list it was opened from, so you could
   * read the file or see the documents, never both. The pane is beside the
   * list instead, and it opens IMMEDIATELY with a loader so the seconds of
   * blob fetch happen somewhere visible rather than behind a dead row press.
   *
   * `useOrgSurface()` is null when this tab is rendered anywhere other than a
   * team or department surface, and the side panel is the answer there —
   * either way the reader is a side surface, never a modal.
   */
  const handleOpenDocument = async (attachment: Attachment) => {
    const doc = rowOf(attachment);
    const download = () => {
      downloadFile({ domain, resourceId, fileId: doc.id, filename: doc.name });
    };

    if (surface) surface.openReader({ title: doc.name, loading: true, resourceId });

    try {
      const result = await viewFile({
        domain,
        resourceId,
        fileId: doc.id,
        filename: doc.name,
        contentType: doc.type,
      });

      if (!result.success || !result.blob) {
        if (surface) surface.closePane('reader');
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: toErrorText(result.error) || t('Failed to load document'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      const contentType = result.contentType || doc.type;

      if (surface) {
        surface.openReader({
          title: doc.name,
          resourceId,
          onDownload: download,
          citation: {
            title: doc.name,
            name: doc.name,
            blob: result.blob,
            blobObject: result.blob,
            contentType,
            type: 'file',
            id: doc.id,
            size: doc.size,
            uploaded_at: doc.uploadedAt,
          },
        });
        return;
      }

      dispatch(showSidePanel({
        type: 'fileReader',
        showSidePanel: true,
        loading: false,
        size: 'lg',
        message: doc.name,
        icon: <PiFileLight className="w-6 h-6 text-fg-muted" />,
        fileUrl: URL.createObjectURL(result.blob),
        fileType: contentType,
      }));
    } catch (error) {
      if (surface) surface.closePane('reader');
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening document. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  };

  const handleDownloadDocument = (attachment: Attachment) => {
    const doc = rowOf(attachment);
    downloadFile({ domain, resourceId, fileId: doc.id, filename: doc.name });
  };

  const handleDeleteDocument = (attachment: Attachment) => {
    if (!onDelete) return;
    dispatch(showModal({
      type: 'confirmation',
      title: t('Delete Document'),
      message: t('Delete "{{name}}"?', { name: attachment.name }),
      subText: t('This document is permanently removed. This cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      showModal: true,
      onConfirm: async () => {
        try {
          await onDelete(attachment.id);
          if (onRefresh) await onRefresh();
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Document deleted successfully'),
            modalType: 'success',
            duration: 3000,
          }));
        } catch {
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete document'),
            modalType: 'error',
            duration: 3000,
          }));
        }
        dispatch(hideModal());
      },
    }));
  };

  const handleDeleteSelected = () => {
    if ((!onBulkDelete && !onDelete) || selectedIds.length === 0) return;
    const toDelete = [...selectedIds];

    dispatch(showModal({
      type: 'confirmation',
      title: t('Delete Documents'),
      message: t('Delete {{count}} document(s)?', { count: toDelete.length }),
      subText: t('They are permanently removed. This cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setIsDeleting(true);
        setDeletingTotal(toDelete.length);
        try {
          if (onBulkDelete) {
            await onBulkDelete(toDelete);
          } else if (onDelete) {
            for (const docId of toDelete) await onDelete(docId);
          }
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('{{count}} document(s) deleted successfully', { count: toDelete.length }),
            modalType: 'success',
            duration: 3000,
          }));
        } catch {
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete documents'),
            modalType: 'error',
            duration: 3000,
          }));
        } finally {
          setIsDeleting(false);
          setDeletingTotal(0);
          setSelectedIds([]);
          if (onRefresh) await onRefresh();
        }
      },
    }));
  };

  const filterActions = useMemo(() => {
    if (!(onBulkDelete || onDelete) || selectedIds.length === 0) return [];
    return [{
      label: t('Delete Selected'),
      onClick: handleDeleteSelected,
      icon: <PiTrashLight className="w-4 h-4 text-danger-500" />,
    }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, onBulkDelete, onDelete, t]);

  /*
    Upload and bulk-delete are whole-list operations: there is nothing
    meaningful to show a row at a time while they run, so the list yields to
    one brand loader rather than half-updating under the user.
  */
  const isBusy = isUploading || isDeleting;

  return (
    <div className="flex flex-col min-h-0 flex-1" data-tour-id={dataTourId}>
      <div className="flex-shrink-0">
        <FilterBarWithTabs
          onSearchChange={setSearchQuery}
          noParentPadding={true}
          noParentHeight={true}
          actions={filterActions}
          selectionCount={selectedIds.length}
          itemCount={visible.length}
          totalItemCount={documents.length}
          itemCountLabel={t('documents')}
        />
      </div>

      {onUpload && !isBusy && (
        <AttachmentDropzone className="mb-3" onUpload={onUpload} />
      )}

      {isBusy ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center py-24 gap-3">
          <Spinner size="md" className="!h-10 !w-10" />
          <span className="text-sm font-medium text-fg-body">
            {isUploading
              ? t('Uploading {{count}} document(s)...', { count: uploadingCount })
              : t('Deleting {{count}} document(s)...', { count: deletingTotal })}
          </span>
        </div>
      ) : (
        <AttachmentList
          attachments={visible}
          loading={loading}
          onOpen={handleOpenDocument}
          onDownload={handleDownloadDocument}
          onRemove={onDelete ? handleDeleteDocument : undefined}
          selectedIds={onBulkDelete || onDelete ? selectedIds : undefined}
          onToggleSelect={
            onBulkDelete || onDelete
              ? (a) =>
                  setSelectedIds((prev) =>
                    prev.includes(a.id) ? prev.filter((i) => i !== a.id) : [...prev, a.id]
                  )
              : undefined
          }
          emptyIcon={<PiFolderLight className="h-6 w-6 text-fg-muted" />}
          emptyTitle={searchQuery ? t('No matching documents') : t('No documents yet')}
          emptyHint={
            searchQuery
              ? t('Try a different search term.')
              : t('Drop files above to add them here.')
          }
        />
      )}
    </div>
  );
};

export default DocumentsTab;
